import React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import Cookies from 'js-cookie'
import ls from '@livesession/browser'
import { UilChartGrowth, UilCheckCircle, UilTimes } from '@iconscout/react-unicons'
import HeaderNewClear from '../partials/HeaderNewClear.jsx'
import FooterNew from '../partials/FooterNew.jsx'
import Carousel from '../partials/Carousel.jsx'
import ModalRecommendsSignUp from '../partials/ModalRecommendsSignUp.jsx'

import { accesslog } from '../../helpers/global.js'
import { publicRecommendsFetchDataApi, publicRecommendsScoreCvsApi } from '../../apis/recommends.js'

import logo1 from '../../images/client_logo/kai.png'
import logo2 from '../../images/client_logo/unicharm.png'
import logo3 from '../../images/client_logo/astra.png'
import logo4 from '../../images/client_logo/telkom-akses.png'
import logo5 from '../../images/client_logo/mufg.png'
import logo6 from '../../images/client_logo/nus.png'
import logo7 from '../../images/client_logo/ntu.png'
import logo8 from '../../images/client_logo/ui.png'
import logo9 from '../../images/client_logo/petrosea.png'
import logo10 from '../../images/client_logo/qiscus.png'
import logo11 from '../../images/client_logo/awalkarir.png'
import logo12 from '../../images/client_logo/pulsifi.png'

const CV_UPLOAD_ALLOWED_EXTENSIONS = ['pdf', 'docx']
const CV_UPLOAD_MAX_FILE_SIZE = 5 * 1024 * 1024
const CV_UPLOAD_MAX_FILES = 10
const JOB_TITLE_MAX_LENGTH = 150
const JOB_DESCRIPTION_MAX_LENGTH = 5000

// Non-real, masked sample candidates shown when a role search returns no live results, or ?sample is set.
const DUMMY_CANDIDATES = [
  {
    preview_id: 'dummy-1',
    masked_name: 'Budi Raharjo',
    location: 'Jakarta',
    last_role: 'Marketing Manager',
    years_experience: 6,
    skills: ['Brand Strategy', 'Campaign Management', 'Content Marketing', 'Google Analytics']
  },
  {
    preview_id: 'dummy-2',
    masked_name: 'Rian Taufik',
    location: 'Bandung',
    last_role: 'Backend Engineer',
    years_experience: 4,
    skills: ['Node.js', 'PostgreSQL', 'REST APIs', 'Docker']
  },
  {
    preview_id: 'dummy-3',
    masked_name: 'Sari Wijaya',
    location: 'Jakarta',
    last_role: 'HR Business Partner',
    years_experience: 8,
    skills: ['Talent Management', 'Employee Relations', 'Workforce Planning']
  },
  {
    preview_id: 'dummy-4',
    masked_name: 'Dewi Kusuma',
    location: 'Surabaya',
    last_role: 'Financial Analyst',
    years_experience: 5,
    skills: ['Financial Modeling', 'Excel', 'Budgeting & Forecasting']
  },
  {
    preview_id: 'dummy-5',
    masked_name: 'Nadia Putri',
    location: 'Jakarta',
    last_role: 'UX Designer',
    years_experience: 3,
    skills: ['Figma', 'User Research', 'Prototyping', 'Design Systems']
  }
]

function getRandomDummyCandidates() {
  return [...DUMMY_CANDIDATES].sort(() => Math.random() - 0.5).slice(0, 3)
}

// Non-real sample CV-scoring results, shown when ?sample is set (the scoring API isn't reachable locally).
const DUMMY_SCORE_RESULTS = [
  {
    id: 'sample-1',
    name: 'Budi Raharjo',
    current_title: 'Marketing Manager',
    years_experience: 6.5,
    skills: ['Brand Strategy', 'Campaign Management', 'Content Marketing', 'Google Analytics'],
    match_score: 96,
    justification: 'Strong brand and campaign management background closely matches the role\'s core requirements. His track record leading multi-channel campaigns and hands-on experience with Google Analytics suggest he can hit the ground running with minimal ramp-up time. Previous performance reviews also highlight strong stakeholder management across regional teams.'
  },
  {
    id: 'sample-2',
    name: 'Rian Taufik',
    current_title: 'Backend Engineer',
    years_experience: 4.2,
    skills: ['Node.js', 'PostgreSQL', 'REST APIs', 'Docker'],
    match_score: 82,
    justification: 'Solid hands-on experience with the exact stack listed in the job description. He has shipped production REST APIs on Node.js and PostgreSQL, and his Docker experience should help the team standardize its deployment workflow. His CV also lists exposure to CI/CD pipelines, which lines up with the team\'s current roadmap.'
  },
  {
    id: 'sample-3',
    name: 'Sari Wijaya',
    current_title: 'HR Business Partner',
    years_experience: 8,
    skills: ['Talent Management', 'Employee Relations', 'Workforce Planning'],
    match_score: 61,
    justification: 'Relevant HR experience, though limited exposure to enterprise-scale workforce planning. Her background is stronger in employee relations than in the strategic planning this role expects, so some ramp-up time should be expected. She has, however, led cross-functional talent reviews at her current company.'
  },
  {
    id: 'sample-4',
    name: 'Dewi Kusuma',
    current_title: 'Financial Analyst',
    years_experience: 5,
    skills: ['Financial Modeling', 'Excel', 'Budgeting & Forecasting'],
    match_score: 35,
    justification: 'Financial modeling skills present, but background doesn\'t align closely with this role. Her experience skews heavily toward budgeting and forecasting for a much smaller team, with little exposure to the analysis this position needs. Most of her tenure was spent on internal reporting rather than external-facing financial analysis.'
  }
]

function getRandomDummyScoreResults() {
  return [...DUMMY_SCORE_RESULTS].sort(() => Math.random() - 0.5)
}

// years_experience can come back as a decimal (e.g. 0.8) -- round to a whole
// number for display, calling out sub-1-year tenure explicitly.
function formatYearsExperience(years) {
  if (years < 1) {
    return 'less than 1 year'
  }
  const rounded = Math.round(years)
  return `${rounded} year${rounded === 1 ? '' : 's'}`
}

const JUSTIFICATION_VISIBLE_SENTENCE_COUNT = 1
const JUSTIFICATION_VISIBLE_MAX_LENGTH = 140

// Returns the first sentence of a justification, hard-capped in length so a
// single very long sentence (no punctuation) can't push the "See more" link off-screen.
function getJustificationPreview(justification) {
  const sentences = justification.trim().match(/[^.!?]+[.!?]+(\s+|$)/g) || [justification]
  const visible = sentences.slice(0, JUSTIFICATION_VISIBLE_SENTENCE_COUNT).join('').trim()
  if (visible.length <= JUSTIFICATION_VISIBLE_MAX_LENGTH) {
    return visible
  }
  return `${visible.slice(0, JUSTIFICATION_VISIBLE_MAX_LENGTH).trim()}…`
}

// 0-59 -> weak (red), 60-79 -> medium (amber), 80-100 -> strong (green).
function getMatchTierClass(matchScore) {
  if (matchScore >= 80) {
    return 'strong'
  }
  if (matchScore >= 60) {
    return 'medium'
  }
  return 'weak'
}

const AVATAR_COLORS = ['#5B0EF5', '#32059D', '#C04051', '#1D8A99', '#B8860B', '#3A6EA5', '#8E44AD', '#2E8B57']

function getAvatarColor(seed) {
  const index = Math.abs(String(seed).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % AVATAR_COLORS.length
  return AVATAR_COLORS[index]
}

function getInitials(text) {
  return String(text)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('')
}

function getFirstName(fullName) {
  return String(fullName).split(' ')[0]
}

class AstrntRecommends extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      mode: 'find',

      // "Find candidates for a role"
      jobTitle: '',
      searchedTitle: '',
      isLoading: false,
      hasSearched: false,
      candidates: [],

      // "I already have candidates"
      scoreJobTitle: '',
      scoreJobDescription: '',
      cvFiles: [],
      cvFileError: '',
      isDraggingCv: false,
      isScoring: false,
      hasScored: false,
      scoreResults: [],
      scoreError: '',

      isSignUpModalOpen: false
    }
  }

  isVisitorLoggedIn = () => {
    return !!Cookies.get('astrnt_user_company')
  }

  // Dev/QA shortcut: ?sample returns mock results instead of calling the API,
  // since the scoring/recommends APIs aren't reachable from local.
  useSampleData = () => {
    return new URLSearchParams(window.location.search).has('sample')
  }

  switchMode = (mode) => {
    if (mode === this.state.mode) {
      return
    }
    const eventByMode = { find: 'tab_find_candidates', have: 'tab_have_candidates' }
    accesslog('astrnt-recommends', eventByMode[mode])
    this.setState({ mode })
  }

  openSignUpModal = (trigger) => {
    accesslog('astrnt-recommends', 'button_see_more_candidates')
    ls.track('signup_modal_shown', { trigger })
    this.setState({ isSignUpModalOpen: true })
  }

  closeSignUpModal = () => {
    this.setState({ isSignUpModalOpen: false })
  }

  trackFieldFilled = (eventName, value, extra) => {
    if (String(value).trim()) {
      ls.track(eventName, extra)
    }
  }

  componentDidMount() {
    window.scrollTo(0, 0)

    // Deep-link support, e.g. ?mode=have to land straight on a non-default tab.
    const modeParam = new URLSearchParams(window.location.search).get('mode')
    if (['find', 'have'].includes(modeParam)) {
      this.setState({ mode: modeParam })
    }

    // Dev/QA shortcut: ?signup opens the sign-up modal directly without
    // needing a real search result to reach it.
    if (new URLSearchParams(window.location.search).has('signup')) {
      this.setState({ isSignUpModalOpen: true })
    }
  }

  handleFindCandidates = () => {
    const { jobTitle } = this.state
    if (!jobTitle.trim()) {
      return
    }

    accesslog('astrnt-recommends', 'button_find_candidates')
    this.setState({ isLoading: true, hasSearched: true, searchedTitle: jobTitle.trim() })

    if (this.useSampleData()) {
      this.setState({ isLoading: false, candidates: getRandomDummyCandidates() })
      return
    }

    publicRecommendsFetchDataApi({ title: jobTitle })
      .then((response) => {
        const { success, candidates } = response.data
        const results = success && candidates && candidates.length ? candidates : getRandomDummyCandidates()
        this.setState({ isLoading: false, candidates: results })
      })
      .catch(() => {
        this.setState({ isLoading: false, candidates: getRandomDummyCandidates() })
      })
  }

  handleCandidateClick = (candidate) => {
    accesslog('astrnt-recommends', 'card_candidate_click', { title: candidate.last_role })
    this.openSignUpModal('candidate_card_click')
  }

  addCvFiles = (incomingFiles) => {
    ls.track('cv_upload_started', { file_count: incomingFiles.length })

    const errors = []
    const addError = (message) => {
      if (!errors.includes(message)) {
        errors.push(message)
      }
    }
    const validFiles = incomingFiles.filter((file) => {
      const extension = file.name.split('.').pop().toLowerCase()
      if (!CV_UPLOAD_ALLOWED_EXTENSIONS.includes(extension)) {
        addError('Only PDF or DOCX files are supported.')
        return false
      }
      if (file.size > CV_UPLOAD_MAX_FILE_SIZE) {
        addError('Each CV must be 5MB or smaller.')
        return false
      }
      return true
    })

    this.setState((prevState) => {
      const combinedFiles = [...prevState.cvFiles, ...validFiles]
      const cvFiles = combinedFiles.slice(0, CV_UPLOAD_MAX_FILES)
      const cappedError = combinedFiles.length > CV_UPLOAD_MAX_FILES ? `You can upload up to ${CV_UPLOAD_MAX_FILES} CVs at a time.` : errors.join(' ')
      return { cvFiles, cvFileError: cappedError }
    }, () => {
      if (this.state.cvFileError) {
        ls.track('cv_upload_failed', { reason: this.state.cvFileError })
      } else if (validFiles.length > 0) {
        ls.track('cv_upload_completed', { file_count: validFiles.length })
      }
    })
  }

  handleCvFilesChange = (e) => {
    const incomingFiles = Array.from(e.target.files || [])
    e.target.value = null // allow re-selecting a file that was removed
    this.addCvFiles(incomingFiles)
  }

  handleCvDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!this.state.isDraggingCv) {
      this.setState({ isDraggingCv: true })
    }
  }

  handleCvDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    this.setState({ isDraggingCv: false })
  }

  handleCvDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const incomingFiles = Array.from((e.dataTransfer && e.dataTransfer.files) || [])
    this.setState({ isDraggingCv: false })
    if (incomingFiles.length) {
      this.addCvFiles(incomingFiles)
    }
  }

  handleRemoveCvFile = (indexToRemove) => {
    this.setState((prevState) => ({
      cvFiles: prevState.cvFiles.filter((_, index) => index !== indexToRemove)
    }))
  }

  handleScoreCandidates = () => {
    const { scoreJobTitle, scoreJobDescription, cvFiles } = this.state
    if (!scoreJobTitle.trim() || cvFiles.length === 0) {
      return
    }

    accesslog('astrnt-recommends', 'button_score_candidates')
    this.setState({ isScoring: true, hasScored: true, scoreError: '', scoreResults: [] })

    if (this.useSampleData()) {
      this.setState({ isScoring: false, scoreResults: getRandomDummyScoreResults() })
      return
    }

    const formData = new FormData()
    formData.append('job_title', scoreJobTitle.trim().slice(0, JOB_TITLE_MAX_LENGTH))
    formData.append('job_description', scoreJobDescription.trim().slice(0, JOB_DESCRIPTION_MAX_LENGTH))
    cvFiles.forEach((file) => formData.append('cvs[]', file))

    publicRecommendsScoreCvsApi(formData)
      .then((response) => {
        const { results } = response.data
        this.setState({ isScoring: false, scoreResults: results || [] })
      })
      .catch((error) => {
        const status = error.response && error.response.status
        let message = 'Something went wrong while scoring your candidates. Please try again.'
        if (status === 429) {
          const retryAfter = error.response.headers && error.response.headers['retry-after']
          message = retryAfter
            ? `Too many requests — please try again in ${retryAfter} seconds.`
            : 'Too many requests. Please wait a moment and try again.'
        } else if (status === 403) {
          message = 'This request could not be processed. Please refresh the page and try again.'
        } else if (status === 400) {
          message = (error.response.data && error.response.data.error) || 'Please check your job title and CV files and try again.'
        }
        this.setState({ isScoring: false, scoreError: message })
      })
  }

  handleResultClick = (result, trigger = 'result_card_click') => {
    accesslog('astrnt-recommends', 'card_result_click', { title: result.current_title })
    this.openSignUpModal(trigger)
  }

  renderLockedList() {
    return (
      <div className="recommends-locked-list" aria-hidden="true">
        {!this.isVisitorLoggedIn() && [0, 1].map((placeholderIndex) => (
          <div className="recommends-card recommends-card-locked" key={`locked-${placeholderIndex}`}>
            <div className="recommends-candidate">
              <span className="recommends-avatar recommends-skeleton-avatar" />
              <div>
                <span className="recommends-skeleton-line recommends-skeleton-line-title" />
                <span className="recommends-skeleton-line recommends-skeleton-line-subtitle" />
                <span className="recommends-skeleton-line recommends-skeleton-line-subtitle" />
              </div>
            </div>
            <div className="recommends-skills">
              <span className="recommends-skeleton-chip" />
              <span className="recommends-skeleton-chip" />
              <span className="recommends-skeleton-chip" />
            </div>
          </div>
        ))}

        <div className="recommends-see-more-overlay">
          <div className="recommends-see-more">
            <button
              type="button"
              className="recommends-see-more-btn"
              data-ls-id="find-locked-banner-button"
              onClick={() => this.openSignUpModal('find_locked_banner_click')}
            >
              <i className="fas fa-lock" /> See more candidates
            </button>
            <p>Unlock more recommended profiles for this role.</p>
          </div>
        </div>
      </div>
    )
  }

  renderScoreLockedList(totalCount) {
    return (
      <div className="recommends-locked-list" aria-hidden="true">
        {!this.isVisitorLoggedIn() && [0, 1].map((placeholderIndex) => (
          <div className="recommends-card recommends-card-locked" key={`score-locked-${placeholderIndex}`}>
            <div className="recommends-candidate">
              <span className="recommends-avatar recommends-skeleton-avatar" />
              <div>
                <span className="recommends-skeleton-line recommends-skeleton-line-title" />
                <span className="recommends-skeleton-line recommends-skeleton-line-subtitle" />
                <span className="recommends-skeleton-line recommends-skeleton-line-subtitle" />
              </div>
            </div>
            <div className="recommends-skills">
              <span className="recommends-skeleton-chip" />
              <span className="recommends-skeleton-chip" />
              <span className="recommends-skeleton-chip" />
            </div>
          </div>
        ))}

        <div className="recommends-see-more-overlay">
          <div className="recommends-see-more">
            <button
              type="button"
              className="recommends-see-more-btn"
              data-ls-id="score-locked-banner-button"
              onClick={() => this.openSignUpModal('score_locked_banner_click')}
            >
              <i className="fas fa-lock" /> See all {totalCount} results
            </button>
            <p>Unlock the full scoring breakdown for every candidate you uploaded.</p>
          </div>
        </div>
      </div>
    )
  }

  renderFindForm() {
    return (
      <React.Fragment>
        <div className="search-container recommends-search">
          <div className="search-input-container recommends-job-title">
            <input
              type="text"
              placeholder="Enter a role title to preview recommended candidates."
              value={this.state.jobTitle}
              onChange={(e) => this.setState({ jobTitle: e.target.value })}
              onBlur={() => this.trackFieldFilled('job_title_filled', this.state.jobTitle, { mode: 'find' })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  this.handleFindCandidates()
                }
              }}
              data-ls-id="find-job-title-input"
            />
          </div>
          <button
            className="search-button recommends-search-button"
            type="button"
            disabled={this.state.isLoading || !this.state.jobTitle.trim()}
            onClick={this.handleFindCandidates}
            data-ls-id="find-submit-button"
          >
            {this.state.isLoading ? 'Searching...' : 'Find Candidates'}
          </button>
        </div>
        <p className="recommends-search-helper">
          {this.state.hasSearched
            ? `Recommended candidates for ${this.state.searchedTitle}`
            : 'Search by role to see top talent matches.'}
        </p>
      </React.Fragment>
    )
  }

  renderScoreForm() {
    const { scoreJobTitle, scoreJobDescription, cvFiles, cvFileError, isDraggingCv, isScoring } = this.state

    return (
      <React.Fragment>
        <div className="recommends-score-form">
          <label htmlFor="cv-score-job-title">Job title <span className="required">*</span></label>
          <input
            id="cv-score-job-title"
            type="text"
            placeholder="e.g: Backend Engineer"
            maxLength={JOB_TITLE_MAX_LENGTH}
            value={scoreJobTitle}
            onChange={(e) => this.setState({ scoreJobTitle: e.target.value })}
            onBlur={() => this.trackFieldFilled('job_title_filled', scoreJobTitle, { mode: 'have' })}
            data-ls-id="score-job-title-input"
          />

          <label htmlFor="cv-score-job-description">Job description</label>
          <textarea
            id="cv-score-job-description"
            placeholder="Paste the job description here."
            rows={3}
            maxLength={JOB_DESCRIPTION_MAX_LENGTH}
            value={scoreJobDescription}
            onChange={(e) => this.setState({ scoreJobDescription: e.target.value })}
            onBlur={() => this.trackFieldFilled('job_description_filled', scoreJobDescription, { mode: 'have' })}
            data-ls-id="score-job-description-input"
          />
          <p className="recommends-field-helper">
            Adding a job description improves match accuracy. A job title alone can be too broad.
          </p>

          <label htmlFor="cv-score-upload">Upload CVs</label>
          <label
            className={`recommends-cv-upload${isDraggingCv ? ' dragging' : ''}`}
            htmlFor="cv-score-upload"
            onDragOver={this.handleCvDragOver}
            onDragEnter={this.handleCvDragOver}
            onDragLeave={this.handleCvDragLeave}
            onDrop={this.handleCvDrop}
          >
            <i className="fas fa-cloud-upload-alt" />
            Click to upload, or drag and drop PDF or DOCX files.
            <input
              id="cv-score-upload"
              type="file"
              accept=".pdf,.docx"
              multiple
              onChange={this.handleCvFilesChange}
              data-ls-id="score-cv-upload-input"
            />
          </label>
          <p className="recommends-field-helper">
            The more candidates you upload, the better the comparison.
          </p>
          {cvFileError && <p className="recommends-field-error">{cvFileError}</p>}

          {cvFiles.length > 0 && (
            <ul className="recommends-cv-file-list">
              {cvFiles.map((file, index) => (
                <li className="recommends-cv-file-item" key={`${file.name}-${index}`}>
                  <UilCheckCircle size="14" />
                  <span className="recommends-cv-file-name" title={file.name}>{file.name}</span>
                  <button
                    type="button"
                    className="recommends-cv-file-remove"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => this.handleRemoveCvFile(index)}
                  >
                    <UilTimes size="14" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            className="recommends-score-submit"
            type="button"
            disabled={isScoring || !scoreJobTitle.trim() || cvFiles.length === 0}
            onClick={this.handleScoreCandidates}
            data-ls-id="score-submit-button"
          >
            <UilChartGrowth size="18" />
            {isScoring ? 'Scoring...' : 'Score candidates'}
          </button>
        </div>
      </React.Fragment>
    )
  }

  renderFindResults() {
    const { isLoading, candidates } = this.state
    if (isLoading || candidates.length === 0) {
      return null
    }

    return (
      <div className="recommends-results">
        {candidates.slice(0, 3).map((candidate, candidateIndex) => (
          <div
            className="recommends-card"
            key={candidate.preview_id}
            onClick={() => this.handleCandidateClick(candidate)}
          >
            <div className="recommends-candidate">
              <span className="recommends-avatar" style={{ backgroundColor: getAvatarColor(candidate.preview_id) }}>
                {getInitials(candidate.masked_name || candidate.last_role)}
              </span>
              <div>
                <h4 className="recommends-candidate-name">{candidate.masked_name ? getFirstName(candidate.masked_name) : `Candidate ${candidateIndex + 1}`}</h4>
                <p className="recommends-role">{candidate.last_role}{candidate.location ? ` • ${candidate.location}` : ''}</p>
                <p className="recommends-experience">{formatYearsExperience(candidate.years_experience)} of experience</p>
              </div>
            </div>

            <div className="recommends-skills">
              {candidate.skills.map((skill, index) => (
                <span className="recommends-skill-chip" key={index}>{skill}</span>
              ))}
            </div>

            <div className="recommends-card-actions">
              <button
                type="button"
                className="recommends-view-profile-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  this.handleCandidateClick(candidate)
                }}
              >
                View Profile
              </button>
            </div>
          </div>
        ))}

        {this.renderLockedList()}
      </div>
    )
  }

  renderScoreResults() {
    const { isScoring, scoreError, scoreResults } = this.state

    if (isScoring) {
      return <p className="text-center recommends-search-helper">Scoring your candidates...</p>
    }

    if (scoreError) {
      return <p className="text-center recommends-field-error">{scoreError}</p>
    }

    if (scoreResults.length === 0) {
      return (
        <p className="text-center recommends-search-helper">
          We couldn't read any of the uploaded CVs. Try a different file format or a clearer scan.
        </p>
      )
    }

    const rankedResults = [...scoreResults].sort((a, b) => b.match_score - a.match_score)

    return (
      <div className="recommends-results">
        {rankedResults.slice(0, 3).map((result, index) => (
          <div
            className="recommends-card"
            key={result.id}
            onClick={() => this.handleResultClick(result)}
          >
            <div className="recommends-candidate">
              <span className="recommends-rank">#{index + 1}</span>
              <span className="recommends-avatar" style={{ backgroundColor: getAvatarColor(result.id) }}>
                {getInitials(result.name)}
              </span>
              <div>
                <h4 className="recommends-candidate-name">{result.name}</h4>
                <p className="recommends-role">{result.current_title}</p>
                <p className="recommends-experience">{formatYearsExperience(result.years_experience)} of experience</p>
              </div>
            </div>

            <div className="recommends-skills-column">
              {result.justification && (
                <div className="recommends-justification-teaser">
                  <p className="recommends-justification">
                    {getJustificationPreview(result.justification)}{' '}
                    <button
                      type="button"
                      className="recommends-see-more-link"
                      onClick={(e) => {
                        e.stopPropagation()
                        this.handleResultClick(result, 'justification_see_more_click')
                      }}
                    >
                      See more..
                    </button>
                  </p>
                </div>
              )}
              <div className="recommends-skills">
                {result.skills.slice(0, 2).map((skill, index) => (
                  <span className="recommends-skill-chip" key={index}>{skill}</span>
                ))}
                {result.skills.length > 2 && (
                  <span className="recommends-skill-chip recommends-skill-chip-more">+{result.skills.length - 2}</span>
                )}
              </div>
            </div>

            <div className="recommends-status-actions">
              <div className="recommends-status">
                <div className={`recommends-score-badge ${getMatchTierClass(result.match_score)}`}>
                  <span className="recommends-score-badge-value">{result.match_score}</span>
                  <span className="recommends-score-badge-max">/100</span>
                </div>
              </div>
            </div>
          </div>
        ))}

        {scoreResults.length > 3 && this.renderScoreLockedList(scoreResults.length)}
      </div>
    )
  }

  render() {
    const { mode } = this.state

    const showResultsSection = mode === 'find' ? this.state.hasSearched : this.state.hasScored
    const hasVisibleResults = mode === 'find'
      ? this.state.hasSearched && !this.state.isLoading && this.state.candidates.length > 0
      : this.state.hasScored && !this.state.isScoring && !this.state.scoreError && this.state.scoreResults.length > 0
    const signUpJobTitle = mode === 'find' ? this.state.searchedTitle : this.state.scoreJobTitle

    return (
      <div id="homepage">
        <HeaderNewClear isOnStatusFor="is-on" />

        <div className="banner ai">
          <section>
            <div className="ban-text talent-acquisition recommends-hero">
              <div className="title-1">See who ASTRNT recommends for your open role.</div>
              <div className="content-desc">
                Let us know what you're looking for and find your best fit candidate.
              </div>

              <div className="recommends-mode-toggle" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'find'}
                  className={mode === 'find' ? 'active' : ''}
                  onClick={() => this.switchMode('find')}
                  data-ls-id="mode-toggle-find"
                >
                  Find candidates for a role
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'have'}
                  className={mode === 'have' ? 'active' : ''}
                  onClick={() => this.switchMode('have')}
                  data-ls-id="mode-toggle-have"
                >
                  I already have candidates
                </button>
              </div>

              {mode === 'find' ? this.renderFindForm() : this.renderScoreForm()}
            </div>
          </section>
        </div>

        {showResultsSection && (
          <div className="recommends-results-section" style={{ position: 'relative' }}>
            <section className="recommends-results-wrap">
              {mode === 'find' ? this.renderFindResults() : this.renderScoreResults()}
            </section>
          </div>
        )}

        <ModalRecommendsSignUp
          isOpen={this.state.isSignUpModalOpen}
          onRequestClose={this.closeSignUpModal}
          jobTitle={signUpJobTitle}
        />

        {!hasVisibleResults && (
          <div className="section-white" style={{ position: 'relative', overflow: 'hidden' }}>
            <section className="p-t-50 p-b-50">
              <h1 className="text-center m-b-24">Everything you need to hire the right people</h1>
              <div className="flex-container flex-center m-t-24">
                <div className="flex-item-left">
                  <div className="sec-desc">
                    <i className="fas fa-bullseye" style={{ fontSize: '32px', color: '#C04051' }} />
                    <h2 style={{ fontWeight: '700' }} className="m-t-16 m-b-16">Role-relevant skills first</h2>
                    <p>See the skills that matter most for the role, right away—so it's easier to spot stronger-fit candidates faster.</p>
                  </div>
                </div>

                <div className="flex-item-left">
                  <div className="sec-desc">
                    <i className="fas fa-briefcase" style={{ fontSize: '32px', color: '#C04051' }} />
                    <h2 style={{ fontWeight: '700' }} className="m-t-16 m-b-16">Experience with context</h2>
                    <p>Preview each candidate's recent role and background to understand how closely they align with what you're hiring for.</p>
                  </div>
                </div>

                <div className="flex-item-left" style={{ flexGrow: 0 }}>
                  <div className="sec-desc">
                    <i className="fas fa-magic" style={{ fontSize: '32px', color: '#C04051' }} />
                    <h2 style={{ fontWeight: '700' }} className="m-t-16 m-b-16">More signal, less guesswork</h2>
                    <p>Go beyond resume screening with recommendations backed by CV, psychometric assessment, and video interview scores data.</p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}

        <div className="section-white relative">
          <section className="partner">
            <div className="part-wrap">
              <ul>
                <li><a><img src={logo1} /></a></li>
                <li><a><img src={logo2} /></a></li>
                <li><a><img src={logo3} /></a></li>
                <li><a><img src={logo4} /></a></li>
                <li><a><img src={logo5} /></a></li>
                <li><a><img src={logo6} /></a></li>
                <li><a><img src={logo7} /></a></li>
                <li><a><img src={logo8} /></a></li>
                <li><a><img src={logo9} /></a></li>
                <li><a><img src={logo10} /></a></li>
                <li><a><img src={logo11} /></a></li>
                <li><a><img src={logo12} /></a></li>
              </ul>
            </div>
          </section>
        </div>

        <div className="section-red triangle-left section-slider" style={{ position: 'relative', overflow: 'hidden' }}>
          <section className="flex-container container-slider-absolute">
            <div className="flex-item-left">
              <div className="vertical-item--center">
                <Carousel />
              </div>
            </div>
            <div className="flex-item-right">
              <div className="vertical-item--center">
                <div className="sec-desc white slider-red">
                  <h1 className="m-t-0 m-b-24">Customer Stories</h1>
                  <p className="text-desc-stories">Explore authentic abilities that go far beyond traditional resumes. ASTRNT is dedicated to showcasing real-world experiences that truly matter, highlighting not just technical skills but also behavioral and situational competencies.</p>
                  <Link to={{ pathname: `/learn` }} onClick={() => {
                    localStorage.setItem('selectedLearnCategory', 'customer-story')
                    localStorage.setItem('fromHeaderNavigation', 'true')
                    accesslog('astrnt-recommends', 'button_view_all_stories')
                  }} className="btn-stories">View All Stories</Link>
                </div>
              </div>
            </div>
          </section>
          <span className="white-curve-bottom-right"></span>
        </div>

        <div className="section-white single-triangle triangle-right recommends-cta" style={{ position: 'relative' }}>
          <section className="p-t-50 p-b-50">
            <div className="ai-interview-start">
              <h1>Ready to Hire with ASTRNT Recommends?</h1>
              <h4>Reduce bias and shortlist faster with role-based, AI-powered talent recommendations.</h4>
              <div className="ban-text-start-demo">
                <div className="ban-link">
                  <Link
                    to={{ pathname: `/register`, query: { plan: 'free' } }}
                    className="try"
                    onClick={() => accesslog('astrnt-recommends', 'button_start_free_plan')}
                  >
                    Start Free Plan
                  </Link>
                  <a
                    onClick={() => {
                      document.getElementById('modal_talk_expert').classList.add('show-cta-modal')
                      accesslog('astrnt-recommends', 'button_talk_to_an_expert')
                    }}
                    className="request-demo-logged"
                  >
                    Talk to an Expert
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>

        <FooterNew />
      </div>
    )
  }
}

const mapStateToProps = (state) => {
  return {
    globalGetLanguages: state.globalGetLanguages,
    user: state.user
  }
}

export default connect(mapStateToProps)(AstrntRecommends)
